import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin, Clock, FileText, CheckCircle2, Navigation, LogIn, Play, RefreshCw,
  CalendarClock, StickyNote, Users, ChevronRight,
} from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import EmbeddedForms from "@/components/forms/EmbeddedForms";
import LiveVisitTracker from "@/components/fleet/LiveVisitTracker";
import { useAuth } from "@/contexts/AuthContext";
import { useVisitTracking } from "@/hooks/useVisitTracking";
import { api, formatApiError } from "@/lib/api";
import { Ear, Headphones } from "lucide-react";

function VisitRecordingsSection({ visitId }) {
  const [items, setItems] = React.useState(null);
  React.useEffect(() => {
    api.get(`/fleet/visits/${visitId}/recordings`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [visitId]);
  if (!items) return null;
  if (items.length === 0) return null;  // hide section entirely when empty to keep sheet tidy
  return (
    <div data-testid="visit-recordings-section">
      <h4 className="text-sm font-semibold text-[#1F2937] flex items-center gap-2 mb-2">
        <Ear className="w-4 h-4 text-[#0B7E8C]" /> Patient recordings ({items.length})
      </h4>
      <div className="space-y-2">
        {items.map((rec) => {
          const Icon = rec.device_type === "otoscope" ? Ear : Headphones;
          return (
            <div key={rec.id} className="border border-[#E5E7EB] rounded-lg p-2.5 flex items-center gap-2.5"
              data-testid={`visit-rec-${rec.id}`}>
              <Icon className="w-5 h-5 text-[#0B7E8C] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1F2937] capitalize">
                  {rec.device_type} · {rec.body_site.replace(/_/g, " ")}
                </div>
                <div className="text-[11px] text-[#94A3B8]">{new Date(rec.recorded_at).toLocaleString()}</div>
                {rec.ai_hint && (
                  <div className="mt-1 text-[11px] text-violet-800 bg-violet-50 border border-violet-100 rounded p-1.5 line-clamp-2">{rec.ai_hint}</div>
                )}
              </div>
              <a href={`/clinical/patients/${rec.patient_id}`} className="text-[11px] font-semibold text-[#0B7E8C] hover:underline">Open</a>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// Status → next action the field clinician can take
const NEXT = {
  unassigned: { label: "Accept", status: "accepted", icon: CheckCircle2 },
  assigned: { label: "Accept", status: "accepted", icon: CheckCircle2 },
  accepted: { label: "Start travel", status: "en_route", icon: Navigation },
  en_route: { label: "Mark arrived", status: "arrived", icon: LogIn },
  arrived: { label: "Begin visit", status: "in_progress", icon: Play },
  in_progress: { label: "Complete visit", status: "completed", icon: CheckCircle2 },
};

// Map a visit status to one of the three workspace buckets
const BUCKET = {
  unassigned: "assigned", assigned: "assigned", accepted: "assigned",
  en_route: "in_progress", arrived: "in_progress", in_progress: "in_progress",
  completed: "completed", cancelled: "completed", missed: "completed",
};
const BUCKET_LABEL = { assigned: "Assigned", in_progress: "In Progress", completed: "Completed" };
const BUCKET_ORDER = ["assigned", "in_progress", "completed"];

// Status transitions that should kick off (or resume) live GPS tracking on
// the provider's device. The first of these clicked prompts for location
// access; subsequent transitions no-op if tracking is already streaming.
const TRACKED_STATUSES = new Set(["en_route", "arrived", "in_progress"]);

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "Unscheduled");

// Convert an ISO string to the value a <input type="datetime-local"> expects (local time)
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 60 * 60 * 1000);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export default function MyVisits() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const statusFilter = params.get("status"); // assigned | in_progress | completed | null
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [detail, setDetail] = useState(null);          // visit currently open in the panel
  const [visitForms, setVisitForms] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [showResch, setShowResch] = useState(false);
  const [reschAt, setReschAt] = useState("");
  const [reschReason, setReschReason] = useState("");

  // Live GPS tracking session for the currently in-progress visit (there
  // should only ever be one at a time for a given clinician).
  const tracking = useVisitTracking();
  const [activeVisitId, setActiveVisitId] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const r = await api.get("/fleet/visits", { params: { clinician_id: user.id, page_size: 200 } });
      const items = r.data.items || [];
      setVisits(items);
      // keep the open panel fresh after an action
      setDetail((d) => (d ? items.find((v) => v.id === d.id) || null : null));
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  // If the provider reloads the page mid-visit, transparently resume the
  // existing GPS tracking session for any visit still on the road.
  useEffect(() => {
    if (loading || tracking.isTracking) return;
    const active = visits.find((v) =>
      v.status === "en_route" || v.status === "arrived" || v.status === "in_progress");
    if (!active) return;
    (async () => {
      const resumed = await tracking.resumeIfActive(active.id);
      if (resumed) setActiveVisitId(active.id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, visits]);

  // Load the forms attached to the visit open in the panel
  useEffect(() => {
    if (!detail || !(detail.form_ids || []).length) { setVisitForms([]); return; }
    api.get("/forms/published-by-ids", { params: { ids: detail.form_ids.join(",") } })
      .then((r) => setVisitForms(r.data.items || [])).catch(() => setVisitForms([]));
  }, [detail?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function openDetail(v) {
    setDetail(v);
    setNoteText(""); setShowResch(false); setReschReason("");
    setReschAt(toLocalInput(v.scheduled_at));
  }

  async function advance(v) {
    const nxt = NEXT[v.status];
    if (!nxt) return;
    setBusy(v.id);
    try {
      // Any "on-visit" transition prompts for GPS access & anchors the
      // geofence at the patient's site (falling back to current GPS if the
      // visit has no coords yet). If the provider denies location we abort so
      // a visit never enters the field without tracking.
      if (TRACKED_STATUSES.has(nxt.status) && !tracking.isTracking) {
        try {
          const siteLat = v.address?.lat ?? v.address?.latitude;
          const siteLng = v.address?.lng ?? v.address?.longitude;
          await tracking.startTracking({
            visitId: v.id, kind: "fleet",
            label: v.patient_name || v.patient_mrn || v.id,
            siteLatitude: typeof siteLat === "number" ? siteLat : undefined,
            siteLongitude: typeof siteLng === "number" ? siteLng : undefined,
          });
          setActiveVisitId(v.id);
        } catch (e) {
          toast.error(e.message || "Location access is required to start the visit.");
          setBusy(""); return;
        }
      }
      // Completing / cancelling a visit closes the tracking session so the
      // dispatcher sees the provider free again immediately.
      if (nxt.status === "completed" || nxt.status === "cancelled") {
        try { await tracking.endTracking(); } catch { /* non-fatal */ }
        setActiveVisitId(null);
      }
      await api.post(`/fleet/visits/${v.id}/status`, { status: nxt.status });
      toast.success(`Visit ${nxt.status.replace(/_/g, " ")}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  }

  async function addNote() {
    if (!detail || !noteText.trim()) return;
    setBusy(detail.id);
    try {
      await api.post(`/fleet/visits/${detail.id}/notes`, { note: noteText.trim() });
      toast.success("Note added");
      setNoteText("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  }

  async function reschedule() {
    if (!detail) return;
    if (!reschReason.trim()) { toast.error("Please provide a reason for rescheduling"); return; }
    if (!reschAt) { toast.error("Pick a new date & time"); return; }
    setBusy(detail.id);
    try {
      await api.post(`/fleet/visits/${detail.id}/reschedule`, {
        scheduled_at: new Date(reschAt).toISOString(), reason: reschReason.trim(),
      });
      toast.success("Visit rescheduled");
      setShowResch(false); setReschReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  }

  // Group visits into the three buckets (respecting an optional URL status filter)
  const grouped = useMemo(() => {
    const g = { assigned: [], in_progress: [], completed: [] };
    for (const v of visits) {
      const b = BUCKET[v.status] || "assigned";
      g[b].push(v);
    }
    return g;
  }, [visits]);

  const visibleBuckets = statusFilter && BUCKET_LABEL[statusFilter] ? [statusFilter] : BUCKET_ORDER;

  const Card = ({ v }) => {
    const nxt = NEXT[v.status];
    const NxtIcon = nxt?.icon;
    return (
      <div className="hah-card p-4 cursor-pointer hover:ring-1 hover:ring-[#0B7E8C33] transition"
        role="button" onClick={() => openDetail(v)} data-testid={`my-visit-${v.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-[#1F2937]">{v.patient_name || "Patient"}</div>
            <div className="text-xs text-[#6B7280] font-mono">{v.patient_mrn}</div>
          </div>
          <StatusBadge status={v.status} />
        </div>
        <div className="mt-3 space-y-1.5 text-sm text-[#475569]">
          <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#94A3B8]" /> {fmtTime(v.scheduled_at)}</div>
          <div className="capitalize"><span className="text-[#94A3B8]">Type:</span> {(v.visit_type || "").replace(/_/g, " ")} · <span className="text-[#94A3B8]">Priority:</span> {v.priority}</div>
          {typeof v.address === "string" && v.address && (
            <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#94A3B8]" /> {v.address}</div>
          )}
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
          {nxt && (
            <Button size="sm" disabled={busy === v.id} onClick={() => advance(v)}
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`my-visit-advance-${v.id}`}>
              {NxtIcon && <NxtIcon className="w-4 h-4 mr-1.5" />} {nxt.label}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => openDetail(v)}
            className="border-[#E5E7EB] text-[#475569]" data-testid={`my-visit-open-${v.id}`}>
            Open <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          {(v.form_ids?.length > 0) && (
            <span className="text-xs text-[#0B7E8C] inline-flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" /> {v.form_ids.length} form(s)
            </span>
          )}
        </div>
      </div>
    );
  };

  const detailNext = detail ? NEXT[detail.status] : null;
  const DetailNextIcon = detailNext?.icon;
  const canEdit = detail && detail.status !== "completed" && detail.status !== "cancelled";

  return (
    <div className="space-y-6" data-testid="my-visits-page">
      <PageHeader
        title={statusFilter && BUCKET_LABEL[statusFilter] ? `My Visits — ${BUCKET_LABEL[statusFilter]}` : "My Visits"}
        subtitle="Visits assigned to you. Open a visit to update status, fill forms, add notes, or reschedule."
      />

      {/* Live GPS tracking overlay (only visible while a visit is in progress) */}
      <LiveVisitTracker
        tracking={tracking}
        onEndVisit={async () => {
          const v = visits.find((x) => x.id === activeVisitId);
          if (!v) return;
          try {
            await api.post(`/fleet/visits/${v.id}/status`, { status: "completed" });
            toast.success("Visit completed");
            setActiveVisitId(null);
            await load();
          } catch (e) { toast.error(formatApiError(e)); }
        }}
      />
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={load} data-testid="my-visits-refresh"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
      </div>

      {loading ? (
        <div className="text-sm text-[#6B7280] py-12 text-center">Loading your visits…</div>
      ) : (
        <>
          {visibleBuckets.map((b) => (
            <div key={b}>
              <h3 className="hah-section-title mb-3" data-testid={`my-visits-section-${b}`}>{BUCKET_LABEL[b]} ({grouped[b].length})</h3>
              {grouped[b].length === 0 ? (
                <div className="hah-card p-8 text-center text-sm text-[#6B7280]" data-testid={`my-visits-empty-${b}`}>No {BUCKET_LABEL[b].toLowerCase()} visits.</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{grouped[b].map((v) => <Card key={v.id} v={v} />)}</div>
              )}
            </div>
          ))}
        </>
      )}

      {/* Visit detail / workspace panel */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="my-visit-detail">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <SheetTitle>{detail?.patient_name || "Visit"}</SheetTitle>
                <SheetDescription>{detail ? `${detail.patient_mrn || ""} · ${(detail.visit_type || "").replace(/_/g, " ")}` : ""}</SheetDescription>
              </div>
              {detail && <StatusBadge status={detail.status} />}
            </div>
          </SheetHeader>

          {detail && (
            <div className="p-6 space-y-6">
              {/* Overview */}
              <div className="space-y-2 text-sm text-[#475569]">
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#94A3B8]" /> {fmtTime(detail.scheduled_at)}</div>
                <div className="capitalize"><span className="text-[#94A3B8]">Priority:</span> {detail.priority}</div>
                {(detail.clinician_names?.length > 0) && (
                  <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#94A3B8]" /> {detail.clinician_names.join(", ")}</div>
                )}
                {typeof detail.address === "string" && detail.address && (
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#94A3B8]" /> {detail.address}</div>
                )}
              </div>

              {/* Status + reschedule actions */}
              <div className="flex flex-wrap items-center gap-2">
                {detailNext && (
                  <Button size="sm" disabled={busy === detail.id} onClick={() => advance(detail)}
                    className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="my-visit-detail-advance">
                    {DetailNextIcon && <DetailNextIcon className="w-4 h-4 mr-1.5" />} {detailNext.label}
                  </Button>
                )}
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => setShowResch((s) => !s)}
                    className="border-[#0B7E8C33] text-[#0B7E8C]" data-testid="my-visit-reschedule-toggle">
                    <CalendarClock className="w-4 h-4 mr-1.5" /> Reschedule
                  </Button>
                )}
              </div>

              {showResch && canEdit && (
                <div className="rounded-lg border border-[#E5E7EB] p-4 space-y-3 bg-[#F8FAFC]" data-testid="my-visit-reschedule-form">
                  <div>
                    <label className="text-xs font-medium text-[#475569]">New date & time</label>
                    <Input type="datetime-local" value={reschAt} onChange={(e) => setReschAt(e.target.value)}
                      className="mt-1" data-testid="my-visit-reschedule-datetime" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#475569]">Reason (required)</label>
                    <Textarea value={reschReason} onChange={(e) => setReschReason(e.target.value)} rows={2}
                      placeholder="Why is this visit being rescheduled?" className="mt-1" data-testid="my-visit-reschedule-reason" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setShowResch(false)}>Cancel</Button>
                    <Button size="sm" disabled={busy === detail.id} onClick={reschedule}
                      className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="my-visit-reschedule-submit">Confirm reschedule</Button>
                  </div>
                </div>
              )}

              {/* Visit notes */}
              <div>
                <h4 className="text-sm font-semibold text-[#1F2937] flex items-center gap-2 mb-2"><StickyNote className="w-4 h-4 text-[#0B7E8C]" /> Visit notes</h4>
                {canEdit && (
                  <div className="space-y-2 mb-3">
                    <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2}
                      placeholder="Add a clinical note for this visit…" data-testid="my-visit-note-input" />
                    <div className="flex justify-end">
                      <Button size="sm" disabled={busy === detail.id || !noteText.trim()} onClick={addNote}
                        className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="my-visit-note-add">Add note</Button>
                    </div>
                  </div>
                )}
                {detail.notes ? (
                  <pre className="text-xs text-[#475569] whitespace-pre-wrap font-sans bg-[#F8FAFC] rounded-lg border border-[#E5E7EB] p-3" data-testid="my-visit-notes-history">{detail.notes.trim()}</pre>
                ) : (
                  <div className="text-xs text-[#94A3B8] italic">No notes yet.</div>
                )}
              </div>

              {/* Linked patient recordings */}
              <VisitRecordingsSection visitId={detail.id} />

              {/* Forms */}
              <div>
                <h4 className="text-sm font-semibold text-[#1F2937] flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-[#0B7E8C]" /> Visit forms</h4>
                {visitForms.length === 0 ? (
                  <div className="text-xs text-[#94A3B8] italic" data-testid="my-visit-no-forms">No forms were selected for this visit.</div>
                ) : (
                  <EmbeddedForms module="patient" entityId={detail.patient_id}
                    entityLabel={`${detail.patient_name} (${detail.patient_mrn})`}
                    forms={visitForms}
                    extraContext={{ visit_id: detail.id, visit_type: detail.visit_type }} />
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
